import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { adminApi, AdminTeamSummary } from '../../lib/adminApi';
import type { AdminUser } from './UsersTab';

interface Props {
  user: AdminUser;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AdminTeamModal({ user, onClose, onSuccess }: Props) {
  const { darkMode } = useDarkMode();
  const [teams, setTeams] = useState<AdminTeamSummary[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    user.teamId ? String(user.teamId) : '',
  );
  const [keepOldTeam, setKeepOldTeam] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'member'>(
    (user.teamRole === 'admin' ? 'admin' : 'member'),
  );

  const isSoloOwner =
    user.teamId !== null &&
    user.teamRole === 'owner' &&
    user.teamSeats?.used === 1;

  const teamChanged = selectedTeamId !== '' && Number(selectedTeamId) !== user.teamId;
  const roleChanged =
    !teamChanged &&
    user.teamId !== null &&
    user.teamRole !== 'owner' &&
    selectedRole !== user.teamRole;

  useEffect(() => {
    adminApi.listTeams()
      .then(setTeams)
      .catch(() => setError('Teams konnten nicht geladen werden'))
      .finally(() => setLoadingTeams(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (teamChanged) {
        await adminApi.assignTeam(user.id, Number(selectedTeamId), keepOldTeam);
      }
      if (roleChanged) {
        await adminApi.setTeamRole(user.id, selectedRole);
      }
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Speichern. Bitte Seite neu laden.');
    } finally {
      setSaving(false);
    }
  };

  const overlay = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50';
  const modal = `rounded-xl border shadow-xl w-full max-w-md mx-4 p-6 ${
    darkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
  }`;
  const label = `block text-xs font-semibold mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const select = `w-full px-3 py-2 rounded-lg border text-sm ${
    darkMode
      ? 'bg-slate-700 border-slate-600 text-slate-100'
      : 'bg-white border-slate-300 text-slate-900'
  }`;
  const section = `border-t pt-4 mt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`;

  return (
    <div className={overlay} onClick={saving ? undefined : onClose}>
      <div className={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-blue-500" />
          <h2 className="font-semibold text-base">Team verwalten</h2>
        </div>
        <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {user.email}
        </p>

        {/* Aktuelles Team */}
        <div className={`text-sm mb-4 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
          <span className="font-medium">Aktuelles Team: </span>
          {user.teamName
            ? <>{user.teamName} <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{user.teamRole}</span></>
            : <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>–</span>
          }
        </div>

        {/* Team zuweisen */}
        <div>
          <label className={label}>Team zuweisen</label>
          {loadingTeams ? (
            <p className="text-sm text-slate-400">Lädt...</p>
          ) : (
            <select
              value={selectedTeamId}
              onChange={e => {
                setSelectedTeamId(e.target.value);
                if (e.target.value === '') setKeepOldTeam(false);
              }}
              className={select}
            >
              <option value="">— kein Wechsel —</option>
              {teams
                .filter(t => t.id !== user.teamId)
                .map(t => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name} ({t.ownerEmail}, {t.memberCount} Mitglieder)
                  </option>
                ))}
            </select>
          )}

          {teamChanged && isSoloOwner && (
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={keepOldTeam}
                onChange={e => setKeepOldTeam(e.target.checked)}
                className="rounded"
              />
              Altes Team behalten (Berichte bleiben)
            </label>
          )}
        </div>

        {/* Rolle im Team */}
        {user.teamId !== null && user.teamRole !== 'owner' && (
          <div className={section}>
            <label className={label}>Rolle im Team</label>
            <select
              value={selectedRole}
              onChange={e => setSelectedRole(e.target.value as 'admin' | 'member')}
              disabled={teamChanged}
              className={`${select} disabled:opacity-50`}
            >
              <option value="member">Mitglied</option>
              <option value="admin">Admin</option>
            </select>
            {teamChanged && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Rolle-Änderung nach Team-Wechsel: bitte separat speichern.
              </p>
            )}
          </div>
        )}

        {/* Fehler */}
        {error && (
          <p className="mt-3 text-sm text-red-500">{error}</p>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg ${
              darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (!teamChanged && !roleChanged)}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
